import * as React from 'react';
import { GetServerSidePropsContext } from 'next';
import { useIntl } from 'react-intl';

import { Layout } from '@components/layout';
import api from '@services/api';
import { Article } from '@definitions/articles.types';
import reportError from '@utils/reportError';
import { ArticleView } from '@components/articles/articleView';
import { getPreviewToken, redirectOnError } from '@utils/helpers';
import { STORE_FRONT_KEY } from '@modules/plp';

export const getServerSideProps = async ({
  params = { slug: '' },
  req,
}: GetServerSidePropsContext<{ slug: string }>) => {
  try {
    const token = getPreviewToken(req.headers.cookie);

    const { data } = await api.get(`/features/${params.slug}`, {
      params: { storefrontKey: STORE_FRONT_KEY },
      headers: { cookie: req.headers.cookie || '', authorization: token },
    });

    if (!data) {
      return { notFound: true };
    }

    return {
      props: { article: data },
    };
  } catch (e) {
    reportError(e);

    return redirectOnError(e);
  }
};

interface SlugProps {
  article: Article;
}

const Slug = ({ article }: SlugProps) => {
  const intl = useIntl();
  if (!article) {
    return null;
  }

  const headerTitle = intl.formatMessage(
    {
      id: 'generalPage.headerTitle',
      defaultMessage: '{title}',
    },
    { title: article.title }
  );

  const headerDescription = intl.formatMessage(
    {
      id: 'article.headerDescription',
      defaultMessage:
        'Discover the latest editorial features on Platform, including artist interviews, studio visits, city guides, artwork guest edits and more.',
    },
    { title: article.title }
  );

  let seoImageUrl = null;
  let firstImagePath;
  if (article.body.length > 0) {
    for (const obj of article.body) {
      if (obj.value && obj.value.products) {
        firstImagePath = obj.value.products[0].images[0].urlThumbnail;
        break;
      }
      if (obj.value && obj.value.image) {
        if (obj.value.image.image && obj.value.image.image.urls && obj.value.image.image.urls.length > 0) {
          firstImagePath = obj.value.image.image.urls[0].url;
          break;
        } else if (obj.value && obj.value.image) {
          firstImagePath = obj.value.image.urls[0].url;
          break;
        }
      }
    }
  }

  if (firstImagePath) {
    seoImageUrl = {
      tags: [],
      title: '',
      noIndex: false,
      noFollow: false,
      image: {
        url: firstImagePath,
        alt: '',
      },
    };
  }

  return (
    <Layout
      title={headerTitle}
      featureNavbar={{ navbarColor: article.navbarColor, navbarTextColor: article.navbarTextColor }}
      defaultDescription={headerDescription}
      seo={article.seo}
      firstProductImageURL={seoImageUrl}
    >
      <ArticleView article={article} />
    </Layout>
  );
};

export default Slug;
